import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FilterIcon, XIcon, ChevronDownIcon, ArrowUpDownIcon, GridIcon, ListIcon } from 'lucide-react';
import { Company } from '../types/company';
import { CompanyCard } from './CompanyCard';
import { industries, cities, fundingRounds } from '../data/mockCompanies';
type SortOption = 'innovationScore' | 'employees' | 'patents' | 'founded' | 'name';
type CompanyListProps = {
  companies: Company[];
  onSelectCompany: (company: Company) => void;
};
export function CompanyList({
  companies,
  onSelectCompany
}: CompanyListProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedFunding, setSelectedFunding] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>('innovationScore');
  const [sortDesc, setSortDesc] = useState(true);
  const filteredAndSorted = useMemo(() => {
    let result = [...companies];
    if (selectedIndustries.length > 0) {
      result = result.filter(c => selectedIndustries.includes(c.industry));
    }
    if (selectedCities.length > 0) {
      result = result.filter(c => selectedCities.includes(c.city));
    }
    if (selectedFunding.length > 0) {
      result = result.filter(c => selectedFunding.includes(c.fundingRound));
    }
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'innovationScore':
          comparison = a.innovationScore - b.innovationScore;
          break;
        case 'employees':
          comparison = a.employees - b.employees;
          break;
        case 'patents':
          comparison = a.patents - b.patents;
          break;
        case 'founded':
          comparison = a.founded - b.founded;
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
      }
      return sortDesc ? -comparison : comparison;
    });
    return result;
  }, [companies, selectedIndustries, selectedCities, selectedFunding, sortBy, sortDesc]);
  const toggleFilter = (value: string, selected: string[], setSelected: (v: string[]) => void) => {
    if (selected.includes(value)) {
      setSelected(selected.filter(v => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  };
  const clearAllFilters = () => {
    setSelectedIndustries([]);
    setSelectedCities([]);
    setSelectedFunding([]);
  };
  const hasActiveFilters = selectedIndustries.length > 0 || selectedCities.length > 0 || selectedFunding.length > 0;
  return <div className="space-y-4" data-id="element-130">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-131">
        <div data-id="element-132">
          <h2 className="text-xl font-semibold text-white" data-id="element-133">
            {filteredAndSorted.length} Companies
          </h2>
          <p className="text-sm text-slate-400" data-id="element-134">
            {hasActiveFilters ? 'Filtered results' : 'Matching your search'}
          </p>
        </div>

        <div className="flex items-center gap-2" data-id="element-135">
          {/* Sort Dropdown */}
          <div className="relative" data-id="element-136">
            <select value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="appearance-none bg-slate-800/50 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-2 pr-8 outline-none focus:border-amber-500/50" data-id="element-137">
              <option value="innovationScore" data-id="element-138">Innovation Score</option>
              <option value="employees" data-id="element-139">Employees</option>
              <option value="patents" data-id="element-140">Patents</option>
              <option value="founded" data-id="element-141">Founded</option>
              <option value="name" data-id="element-142">Name</option>
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" data-id="element-143" />
          </div>

          <button onClick={() => setSortDesc(!sortDesc)} className="p-2 bg-slate-800/50 border border-slate-700 hover:border-slate-600 rounded-lg text-slate-400 hover:text-white transition-colors" aria-label={sortDesc ? 'Sort ascending' : 'Sort descending'} data-id="element-144">
            <ArrowUpDownIcon className="w-4 h-4" data-id="element-145" />
          </button>

          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${showFilters || hasActiveFilters ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-slate-800/50 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'}`} data-id="element-146">
            <FilterIcon className="w-4 h-4" data-id="element-147" />
            Filters
            {hasActiveFilters && <span className="px-1.5 py-0.5 bg-amber-500 text-slate-900 rounded text-xs font-bold" data-id="element-148">
                {selectedIndustries.length + selectedCities.length + selectedFunding.length}
              </span>}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      <AnimatePresence data-id="element-149">
        {showFilters && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden" data-id="element-150">
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 space-y-4" data-id="element-151">
              <div className="flex items-center justify-between" data-id="element-152">
                <h3 className="text-sm font-medium text-white" data-id="element-153">
                  Filter Results
                </h3>
                {hasActiveFilters && <button onClick={clearAllFilters} className="text-xs text-amber-500 hover:text-amber-400" data-id="element-154">
                    Clear all
                  </button>}
              </div>

              {/* Industry Filter */}
              <div data-id="element-155">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2" data-id="element-156">
                  Industry
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-157">
                  {industries.map(industry => <button key={industry} onClick={() => toggleFilter(industry, selectedIndustries, setSelectedIndustries)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${selectedIndustries.includes(industry) ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-slate-700/50 text-slate-400 border border-transparent hover:text-white'}`} data-id="element-158">
                      {industry}
                    </button>)}
                </div>
              </div>

              {/* City Filter */}
              <div data-id="element-159">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2" data-id="element-160">
                  City
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-161">
                  {cities.slice(0, 20).map(city => <button key={city} onClick={() => toggleFilter(city, selectedCities, setSelectedCities)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${selectedCities.includes(city) ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-slate-700/50 text-slate-400 border border-transparent hover:text-white'}`} data-id="element-162">
                      {city}
                    </button>)}
                </div>
              </div>

              {/* Funding Filter */}
              <div data-id="element-163">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2" data-id="element-164">
                  Funding Round
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-165">
                  {fundingRounds.map(round => <button key={round} onClick={() => toggleFilter(round, selectedFunding, setSelectedFunding)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${selectedFunding.includes(round) ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-slate-700/50 text-slate-400 border border-transparent hover:text-white'}`} data-id="element-166">
                      {round}
                    </button>)}
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Company List */}
      <div className="space-y-3" data-id="element-167">
        {filteredAndSorted.map((company, index) => <CompanyCard key={company.id} company={company} index={index} onClick={() => onSelectCompany(company)} data-id="element-168" />)}

        {filteredAndSorted.length === 0 && <div className="text-center py-12" data-id="element-169">
            <p className="text-slate-400" data-id="element-170">No companies match your filters</p>
            <button onClick={clearAllFilters} className="mt-2 text-amber-500 hover:text-amber-400 text-sm" data-id="element-171">
              Clear filters
            </button>
          </div>}
      </div>
    </div>;
}